import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Closure and currying magic for cleaner javascript",
  "description": "",
  "published": "2012-03-14T00:00:00.000Z",
  "redirect_from": ["/blog/closure-and-currying-magic-for-cleaner-javascript/swizec/3981"],
  "categories": "closures, functions, JavaScript, jQuery, Languages, Plug-in (computing), Programming, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Sometimes you find a piece of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`javascript`}</a>{`, a perfect piece of javascript, it does `}<em parentName="p">{`exactly`}</em>{` what you need! Perfect! You could spend hours, even days, getting something to work and someone's already done it, shared the code on the internets even!`}</p>
    <p>{`Hooray!`}</p>
    <p>{`But ... the code looks something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// to populate later
var data = [],
  oldData = [],
  semiOldData = [];
var a_switch = "blah";

function do_something() {
  // use subset of globals
}

function something_else() {
  // use some other globals
}

// etc.

function update() {
  // use all of the above
}
`}</code></pre>
    <p>{`The code works ... if you only need to use it once. What if you need two independent widgets? Well, you could take the time to recode all of that so it becomes `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Purely_functional",
        "title": "Purely functional"
      }}>{`purely functional`}</a>{`.`}</p>
    <p>{`But that's going to be a problem - the code heavily relies on state. Ok maybe object oriented? Object oriented kind of sucks in javascript and you'd have to recode all of that, have to perfectly understand it etc.`}</p>
    <p>{`Pain in the arse!`}</p>
    <h2 {...{
      "id": "closure-magic"
    }}>{`Closure magic`}</h2>
    <p>{`There's a better way - put it in a closure.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var my_widget = function (data_function) {
  // to populate later
  var data = [],
    oldData = [],
    semiOldData = [];
  var an_index = 1;

  function do_something() {
    // use subset of globals
  }

  function something_else() {
    // use some other globals
  }

  // etc.

  function update(index) {
    an_index = index;
    data = data_function();
    // use all of the above
  }

  return update;
};
`}</code></pre>
    <p>{`Now you get to create the widget with a simple function call. Hooray!`}</p>
    <p>{`And the function returns its own update function so you can run an update whenever you need. All the state and weirdness is neatly packaged inside the closure, the code itself doesn't need to know anything about the outside state.`}</p>
    <p>{`By changing the update function a little bit you can even affect the internal state of the closure by calling the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Currying",
        "title": "Currying"
      }}>{`curried function`}</a>{`.`}</p>
    <h2 {...{
      "id": "closures-upon-closure-computer-science"
    }}>{`Closures upon `}<a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Closure_%28computer_science%29",
        "title": "Closure (computer science)"
      }}>{`closures`}</a></h2>
    <p>{`Here's where it gets even more magical. Stacking closures to achieve true greatness.`}</p>
    <p>{`Code is the best way to explain what I mean ...`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// these functions are used to slice up data
var slice_functions = {
        students: function (item) { return !!item.cs_student; },
        others: function (item) { return !item.cs_student; },
        everyone: function (item) { return true; }
    },
        slice_func = slice_functions.students,

// these functions provide cleaned up data
// all must use the same main slice_func
        data_functions = {
            year_vs_pay: function (year) {
                var data = DATA.filter(slice_func).filter(function (item) {
                    return item.years_study == year;
                }),
                // ...
                return fin_data;
            },
            study_vs_pay: function (study) {
                var data = DATA.filter(slice_func).filter(function (item) {
                    return item.study_time == study;
                }),
                // ...
                return fin_data;
            },
            // ...
`}</code></pre>
    <p>{`Those are our data functions, when creating new widgets we can provide them as the `}<em parentName="p">{`data_function`}</em>{` argument shown above.`}</p>
    <p>{`Now comes the tricky part - we can externally change the `}<em parentName="p">{`slice_func`}</em>{` and all the data_functions (now safely tucked inside closures) `}<strong parentName="p">{`change`}</strong>{` their behaviour by using the new slice_function since variables inside closures are pointers rather than copies.`}</p>
    <p>{`Magic!`}</p>
    <p>{`We have reached a situation where all code is tucked neatly inside closures. The closures themselves have no knowledge of the outside world, there are no flags they need to check, no external data anywhere. All they care about is running the data function and doing their thing.`}</p>
    <p>{`And yet, through the magic of closures, we get to globally change their behaviour without worrying about internal state `}<em parentName="p">{`or`}</em>{` having to call a specific function to do something on an object.`}</p>
    <p>{`To me this feels cleaner than any abstraction `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Object-oriented_programming",
        "title": "Object-oriented programming"
      }}>{`object oriented programming`}</a>{` has ever come up with.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.adafruit.com/blog/2012/03/14/breakout-connect-your-arduino-sensors-and-actuators-to-javascript/"
        }}>{`Breakout - Connect your Arduino sensors and actuators to javascript`}</a>{` (adafruit.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9684105/calling-javascript-functions-from-a-java-applet"
        }}>{`Calling Javascript functions from a Java Applet`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/the-commonest-javascript-bug/swizec/3740"
        }}>{`The commonest javascript bug`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.websanova.com/tutorials/jquery/building-collections-efficiently-using-jquery"
        }}>{`Building Collections Efficiently Using jQuery`}</a>{` (websanova.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.websanova.com/tutorials/jquery/10-coding-tips-to-write-superior-jquery-plugins"
        }}>{`10 Coding Tips to Write Superior jQuery Plugins`}</a>{` (websanova.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9058248/good-examples-for-using-a-closure-in-javascript"
        }}>{`Good examples for using a Closure in Javascript`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://lostechies.com/derekgreer/2012/02/17/javascript-closures-explained/"
        }}>{`JavaScript Closures Explained`}</a>{` (lostechies.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=dd5a4e07-700e-44df-b113-8195cf2d3133",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      